/**
 * RequestInfo. Contains data that can be extracted from a request (url,
 * headers, cookies, user agent, etc.). This information is coming from Fastly
 * headers and is used to populate this object.
 *
 * NOTE(frontend-infra): Please do not add properties to this, or any other,
 * info objects without first consulting with us!
 */

import {
    kaCurriculum,
    kaFastlyCountry,
    kaIsBot,
    kaIsE2E,
    kaIsIOS,
    kaIsMobileApp,
    kaIsPhone,
    kaIsTablet,
    kaIsUnsupportedBrowser,
    kaLocale,
    kaLocaleStatus,
    kaRegion,
} from "./fastly-data.ts";
import InitialRequestUrl from "./initial-request-url.ts";

/**
 * NOTE(frontend-infra): Tests that are using RequestInfo should mock the
 * accessors with:
 *      jest.spyOn(RequestInfo, "property", "get").mockReturnValue("value")
 *
 * Do not rely on the internal implementation of RequestInfo.
 */

// TODO(mahtab, CP-7774) - Urdu has been removed from the RTL locales temporarily as
// they want to have a site without needing to wait for RTL support.
// sync-start:rtl-locales 540545200 ./libs/dev/page-template/src/make-page.ts
export const RTL_LOCALES = ["en-x-rtl", "he", "ar", "fa", "fa-af"];
// sync-end:rtl-locales

// sync-start:dev-domains 943708748 https://github.com/Khan/webapp/blob/master/dev/server/Makefile
export const _DEV_DOMAINS = ["localhost", "khanacademy.dev"] as const;
// sync-end:dev-domains

type LocaleStatus = "BEGINNING" | "LITE" | "TEST" | "LIVE" | "ROCK_STAR" | null;

const RequestInfo = {
    get IS_DEV_SERVER(): boolean {
        return _DEV_DOMAINS.some(
            (domain) =>
                InitialRequestUrl.hostname === domain ||
                InitialRequestUrl.hostname.endsWith(`.${domain}`),
        );
    },
    get isE2E(): boolean {
        return kaIsE2E();
    },
    get isBot(): boolean {
        return kaIsBot();
    },
    get isIOS(): boolean {
        return kaIsIOS();
    },
    get isIPad(): boolean {
        return RequestInfo.isIOS && RequestInfo.isTablet;
    },
    get isMobile(): boolean {
        return RequestInfo.isPhone || RequestInfo.isTablet;
    },
    get isPhone(): boolean {
        return kaIsPhone();
    },
    get isTablet(): boolean {
        return kaIsTablet();
    },
    get isMobileApp(): boolean {
        return kaIsMobileApp();
    },
    get isUnsupportedBrowser(): boolean {
        return kaIsUnsupportedBrowser();
    },
    get kaLocale(): string {
        return kaLocale() || "en";
    },
    get kaLocaleStatus(): LocaleStatus {
        return (kaLocaleStatus() as LocaleStatus) || null;
    },
    get isRTL(): boolean {
        return RTL_LOCALES.includes(RequestInfo.kaLocale);
    },
    get usePreviewFMS(): boolean {
        return InitialRequestUrl.configParams.preview === "1";
    },
    get remoteCountry(): string {
        return (
            InitialRequestUrl.configParams.country || kaFastlyCountry() || "ZZ"
        );
    },
    // Check the ?region= query param first before checking the Fastly
    // header as that will always override what comes from the header.
    // "ZZ" indicates an unknown region.
    get kaRegion(): string {
        return InitialRequestUrl.configParams.region || kaRegion() || "ZZ";
    },
    // Check the ?curriculum= query param first before checking the Fastly
    // header as that will always override what comes from the header
    get curriculum(): string {
        return (
            InitialRequestUrl.configParams.curriculum || kaCurriculum() || ""
        );
    },
    get embedded(): boolean {
        return InitialRequestUrl.configParams.embedded === "1";
    },
    get khanClassroomEmbedded(): boolean {
        return InitialRequestUrl.configParams.khanClassroomEmbedded === "1";
    },
    get isExtension(): boolean {
        return InitialRequestUrl.configParams.extension === "1";
    },
};

export default RequestInfo;
